import { notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { ISolicitacaoDeRecuperacaoReceitas, IUploadHistorico } from "./interfaces";
import RecuperacaoReceitas from "../../services/http/services/RecuperacaoReceitas/RecuperacaoReceitas";
import axios from "axios";
import { getUsuario } from "../../services/token/usuario";

export const useRecuperacaoReceitas = () => {
    const [observacao, setObservacao] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [arrayStatus, setArrayStatus] = useState<{ status: string[] }>({ status: [] });
    const [selectedRow, setSelectedRow] = useState<ISolicitacaoDeRecuperacaoReceitas | null>(null);
    const [loadingModal, setLoadingModal] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);
    const [dataSolicitacao, setDataSolicitacao] = useState<ISolicitacaoDeRecuperacaoReceitas[]>([]);
    const [processandoArquivo, setProcessandoArquivo] = useState(false);
    const [dataUploadHistorico, setDataUploadHistorico] = useState<IUploadHistorico[]>([]);

    const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const usuario = getUsuario();
    const usuarioNome = usuario?.nome || null;

    const signal = axios.CancelToken.source();

    const cancelPolling = useCallback(() => {
        if (pollingTimerRef.current !== null) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);

    const closeModal = () => {
        setIsModalOpen(false);
        setObservacao('');
        setSelectedStatus(null);
    };

    const showModal = useCallback((record: ISolicitacaoDeRecuperacaoReceitas): void => {
        setLoadingModal(true);
        setSelectedRow(record);
        setIsModalOpen(true);
        setTimeout(() => {
            setLoadingModal(false);
        }, 500);
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            setLoadingTable(true)

            const data = await RecuperacaoReceitas.getStatusSolicitacoes(signal.token);

            setArrayStatus(data);

        } catch (error) {

            let msgErro: any = (error as Error);
            if (msgErro?.message != 'Requisicao cancelada!') {
                setLoadingTable(false)
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possivel buscar os dados!',
                });
            }
        } finally {
            setLoadingTable(false);
        }
    }, []);

    const loadDataTableSolicitacao = useCallback(async () => {
        try {
            setLoadingTable(true)

            const data = await RecuperacaoReceitas.getSolicitacoes(signal.token);
            setDataSolicitacao(data);

        } catch (error) {

            let msgErro: any = (error as Error);
            if (msgErro?.message != 'Requisicao cancelada!') {
                setLoadingTable(false)
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possivel buscar os dados!',
                });
            }

        } finally {
            setLoadingTable(false);
        }
    }, []);

    const loadDataTableUploadHistorico = useCallback(async (iniciarPolling = false) => {
        try {
            if (processandoArquivo) setLoadingTable(true);

            const data = await RecuperacaoReceitas.getHistoricoUpload(signal.token);
            setDataUploadHistorico(data);

            const temProcessando = Array.isArray(data) && data.some(
                (item) => item.status === 'Processando'
            );

            setProcessandoArquivo(temProcessando);

            if (temProcessando) {
                cancelPolling();
                pollingTimerRef.current = setTimeout(() => {
                    loadDataTableUploadHistorico(true);
                }, 10000);
            } else {
                cancelPolling();
            }

        } catch (error) {
            let msgErro: any = (error as Error);
            if (msgErro?.message != 'Requisicao cancelada!') {
                setLoadingTable(false)
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possivel buscar os dados!',
                });
            }
        } finally {
            setLoadingTable(false);
        }
    }, [cancelPolling, processandoArquivo]);

    const handleUpdateStatusSolicitacao = useCallback(async (id: string, status: string, id_ibge: string) => {
        try {
            setLoadingButtonId(id);

            await RecuperacaoReceitas.putStatusSolicitacao(id, status, id_ibge, usuarioNome, signal.token);
            window.dispatchEvent(new Event('atualizarContagemBalon'));
            await loadDataTableSolicitacao();

            notification.success({
                message: 'Sucesso',
                description: 'Status da solicitação atualizado com sucesso!',
            });

        } catch (error) {
            notification.error({
                message: 'Erro',
                description: 'Falha ao atualizar status da solicitação!',
            });
        } finally {
            setLoadingButtonId(null);
        }
    }, []);

    const handlePostObservacao = useCallback(async (id: string, id_ibge: string, observacao: string) => {
        try {
            setLoadingButtonId(id);

            await RecuperacaoReceitas.postObservacaoSolicitacao(id, id_ibge, usuarioNome, observacao, signal.token);
            window.dispatchEvent(new Event('atualizarContagemBalon'));
            await loadDataTableSolicitacao();

            notification.success({
                message: 'Sucesso',
                description: 'Observação adicionada com sucesso!',
            });

        } catch (error) {
            notification.error({
                message: 'Erro',
                description: 'Falha ao atualizar status da solicitação!',
            });
        } finally {
            setLoadingButtonId(null);
        }
    }, []);

    const handleUpload = useCallback(async (fonte_id: number | null, file: File) => {
        try {
            setLoadingUpload(true);
            if (!fonte_id) {
                notification.warning({
                    message: 'Erro',
                    description: 'Selecione uma fonte primeiro!',
                });
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            const response = await RecuperacaoReceitas.uploadFile(fonte_id, formData, usuarioNome, signal.token);

            await loadDataTableUploadHistorico(true);

            const message = response?.data?.message || response?.message || 'Arquivo enviado com sucesso!';

            notification.success({
                message: 'Sucesso',
                description: message,
            });

        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Falha ao enviar arquivo';

            notification.error({
                message: 'Erro',
                description: message,
            });
        } finally {
            setLoadingUpload(false);
        }
    }, [loadDataTableUploadHistorico]);

    const handleDownload = useCallback(async (id: string, fileUrl: string) => {
        try {
            setLoadingButtonId(id);

            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", "");
            link.target = "_blank";

            document.body.appendChild(link);
            link.click();
            link.remove();

        } catch (error) {
            notification.error({
                message: 'Erro',
                description: 'Falha ao baixar arquivo',
            });
        } finally {
            setLoadingButtonId(null);
        }
    }, []);

    const handleSaveFooter = useCallback(async () => {
        if (!selectedRow) return;

        if (selectedStatus) {
            await handleUpdateStatusSolicitacao(
                selectedRow._id,
                selectedStatus,
                String(selectedRow.id_ibge)
            );
        }

        if (observacao) {
            await handlePostObservacao(
                selectedRow._id,
                String(selectedRow.id_ibge),
                observacao
            );
        }

        setSelectedStatus(null);
        setObservacao("");
        closeModal();
    }, [
        selectedRow,
        selectedStatus,
        observacao,
        handleUpdateStatusSolicitacao,
        handlePostObservacao,
        closeModal
    ]);

    useEffect(() => {

        loadStatus();
        loadDataTableSolicitacao();
        loadDataTableUploadHistorico();

    }, []);

    useEffect(() => () => {
        signal.cancel("Requisicao cancelada!");
        cancelPolling();
    }, []);

    return {
        observacao,
        arrayStatus,
        selectedRow,
        isModalOpen,
        loadingModal,
        loadingTable,
        loadingUpload,
        selectedStatus,
        dataSolicitacao,
        loadingButtonId,
        dataUploadHistorico,
        showModal,
        closeModal,
        handleUpload,
        setObservacao,
        handleDownload,
        handleSaveFooter,
        setSelectedStatus,
        handlePostObservacao,
        handleUpdateStatusSolicitacao,
    };
}
